import { useApolloClient } from '@apollo/client'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import SearchFilterGrid from 'components/SearchFilterGrid'
import { GoodProduct, Permission } from '../../../graphql'
import getColumns from './columns'
import useProductsDataQuery, { ProductRow, ProductsSearchRequest } from './hooks/useProductsDataQuery'
import { ReactElement } from 'react'
import { useDispatchSetSelectedModal } from '../../../contexts/modal-context/modal-context'
import { getProductAddModal, getProductEditModal, getProductMergeModal } from './action-modals'
import PermissionsCheck from 'components/PermissionsCheck'
import PageTitle from 'views/components/PageTitle/PageTitle'
import usePermissions from 'hooks/usePermissions'
import { checkPermissions } from 'components/PermissionsCheck/helpers'
import { heapEvent } from '@equipmentshare/heaper'

export default function PartsAlpha(): ReactElement {
	const { userPermissions } = usePermissions( [ Permission.CanEditPart ] )
	const canEdit = checkPermissions( userPermissions, [ Permission.CanEditPart ] )
	const setSelectedModal = useDispatchSetSelectedModal()
	const client = useApolloClient()

	// The part mutations don't touch the products connection, so refetch it after one lands.
	const refreshProducts = async() => {
		await client.refetchQueries( { include: [ 'Products' ] } )
	}

	const openAddModal = () => {
		heapEvent( 'Inventory - Parts Catalog - Click - Add Part', {} )
		setSelectedModal( getProductAddModal( refreshProducts ) )
	}

	const openMergeModal = ( part: GoodProduct ) => {
		heapEvent( 'Inventory - Edit Part Modal - Click - Merge Parts', {} )
		setSelectedModal( getProductMergeModal( part, refreshProducts ) )
	}

	const openEditModal = ( part: GoodProduct ) => {
		heapEvent( 'Inventory - Parts Catalog - Click - Edit', {} )
		setSelectedModal( getProductEditModal( part, openMergeModal, refreshProducts ) )
	}

	const columns = getColumns( canEdit, openEditModal )

	return (
		<Box p={3}>
			<Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
				<PageTitle title="Product Catalog" />
				<PermissionsCheck permissionsToCheck={[ Permission.CanCreatePart ]}>
					<Button variant="contained" onClick={openAddModal}>Add product</Button>
				</PermissionsCheck>
			</Stack>
			<SearchFilterGrid<ProductRow, ProductsSearchRequest>
				dataGridName="parts-catalog-table"
				filterStateMode="url"
				columns={columns}
				pageSizeOptions={[ 25, 50, 100 ]}
				useFetchQuery={useProductsDataQuery}
				searchPlaceholder="Search products by manufacturer number or description..."
				getRowId={( row ) => row.id}
				initialState={{
					columns: {
						columnVisibilityModel: {
							upc: false,
							year: false,
						},
					},
				}}
			/>
		</Box>
	)
}
